import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import Tabs from '@elements/Tab/Tabs';
import { Input } from '@elements/Input/Input';
import { Select } from '@elements/Dropdown/Select';
import StateSelect from '@elements/Dropdown/StateSelect';
import type { ITab } from '@elements/Tab';
import type { DropdownOption } from '@elements/Dropdown';
import { Elements } from '@elements/index.ts';
import { getCountiesByState } from '@/utils/counties';

export interface ShopPlansTabsProps {
    onShopPlans?: (data: {
        zipCode?: string;
        age?: string;
        ages?: string[];
        familyMembersCount?: number;
        state?: string;
        county?: string;
    }) => void;
}

export const ShopPlansTabs = ({ onShopPlans }: ShopPlansTabsProps) => {
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<string>('age-zip');
    const [zipCode, setZipCode] = useState<string>('');
    const [ages, setAges] = useState<string[]>(['']);
    const [selectedState, setSelectedState] = useState<string>('');
    const [selectedCounty, setSelectedCounty] = useState<string>('');
    const [countyOptions, setCountyOptions] = useState<DropdownOption[]>([]);
    const [isLoadingCounties, setIsLoadingCounties] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<{
        zipCode?: boolean;
        age?: boolean;
        state?: boolean;
    }>({});

    useEffect(() => {
        const urlZipCode = searchParams.get('zipCode');
        const urlAges = searchParams.getAll('age');
        const urlState = searchParams.get('state');
        const urlCounty = searchParams.get('county');

        if (urlZipCode || urlAges.length > 0) {
            setActiveTab('age-zip');
            if (urlZipCode) setZipCode(urlZipCode);
            if (urlAges.length > 0) {
                setAges(urlAges);
            }
        } else if (urlState || urlCounty) {
            setActiveTab('state-county');
            if (urlState) setSelectedState(urlState);
            if (urlCounty) setSelectedCounty(urlCounty);
        }
    }, [searchParams]);

    useEffect(() => {
        if (!selectedState) {
            setCountyOptions([]);
            return;
        }

        setIsLoadingCounties(true);
        getCountiesByState(selectedState)
            .then((options) => {
                setCountyOptions(options);
                setSelectedCounty((prev) => {
                    if (prev && !options.find((opt) => opt.value === prev)) {
                        return '';
                    }
                    return prev;
                });
            })
            .catch((error) => {
                console.error('Failed to load counties:', error);
                setCountyOptions([]);
            })
            .finally(() => {
                setIsLoadingCounties(false);
            });
    }, [selectedState]);

    const handleShopPlans = () => {
        setValidationError(null);
        setFieldErrors({});

        if (activeTab === 'age-zip') {
            const hasZipCode = zipCode.trim() !== '';
            const validAges = ages.filter((value) => value.trim() !== '');
            const primaryAge = validAges.length > 0 ? validAges[0] : undefined;

            if (!hasZipCode) {
                setValidationError('Fill in the required fields to continue');
                setFieldErrors({
                    zipCode: true,
                    age: false,
                });
                return;
            }

            const familyMembersCount = validAges.length > 0 ? validAges.length : undefined;

            onShopPlans?.({
                zipCode,
                age: primaryAge,
                ages: validAges.length > 0 ? validAges : undefined,
                familyMembersCount,
            });
        } else {
            if (!selectedState.trim()) {
                setValidationError('Fill in the required fields to continue');
                setFieldErrors({
                    state: true,
                });
                return;
            }

            onShopPlans?.({ state: selectedState, county: selectedCounty });
        }
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleShopPlans();
    };

    const tabs: ITab[] = [
        {
            value: 'age-zip',
            label: 'Search by Zip & Age',
            content: (
                <form onSubmit={handleFormSubmit} className="flex min-w-0 flex-col gap-2 sm:gap-4">
                    <div className="flex min-w-0 flex-wrap gap-2 sm:gap-4 md:flex-wrap md:justify-start md:items-end">
                        <div className="w-[80px] shrink-0">
                            <Input
                                id="zip-code"
                                label="Zip Code"
                                type="text"
                                placeholder="51234"
                                value={zipCode}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                                    setZipCode(value);
                                    if (fieldErrors.zipCode) {
                                        setFieldErrors((prev) => ({ ...prev, zipCode: false }));
                                        setValidationError(null);
                                    }
                                }}
                                error={fieldErrors.zipCode ? '' : undefined}
                                inputWrapClassName={fieldErrors.zipCode ? '!border-red-500' : ''}
                            />
                        </div>
                        <div className="flex items-end gap-1 flex-nowrap">
                            {ages.map((age, index) => {
                                const isLast = index === ages.length - 1;
                                const isPrimaryAge = index === 0;

                                return (
                                    <div key={index} className="flex shrink-0 items-end gap-2">
                                        <div className="w-[50px] shrink-0">
                                            <Input
                                                id={index === 0 ? 'age' : `age-${index + 1}`}
                                                label={isPrimaryAge ? "Age" : undefined}
                                                placeholder="27"
                                                value={age}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                                                    const nextAges = [...ages];
                                                    nextAges[index] = value;
                                                    setAges(nextAges);
                                                    if (isPrimaryAge && fieldErrors.age) {
                                                        setFieldErrors((prev) => ({ ...prev, age: false }));
                                                        setValidationError(null);
                                                    }
                                                }}
                                                error={isPrimaryAge && fieldErrors.age ? '' : undefined}
                                                inputWrapClassName={
                                                    isPrimaryAge && fieldErrors.age ? '!border-red-500' : ''
                                                }
                                            />
                                        </div>
                                        {isLast && ages.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => setAges((prev) => prev.slice(0, -1))}
                                                className="mb-[13px] cursor-pointer"
                                                aria-label="Remove family member"
                                            >
                                                <svg
                                                    width="12"
                                                    height="12"
                                                    viewBox="0 0 12 12"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M11.25 0.75L0.75 11.25"
                                                        stroke="black"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M11.25 11.25L0.75 0.75"
                                                        stroke="black"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            {ages.length < 6 && (
                                <div className="flex items-end">
                                    <Elements.Button
                                        variant="link"
                                        type="button"
                                        className="h-[27px] rounded-lg p-0 px-2 whitespace-nowrap no-underline mb-[6px]"
                                        onClick={() => setAges((prev) => [...prev, ''])}
                                    >
                                        <span className="text-base text-brand-700 sm:text-base">+ Add Family Member</span>
                                    </Elements.Button>
                                </div>
                            )}
                        </div>
                    </div>
                    {validationError && <div className="-mt-2 text-sm font-medium text-red-600">{validationError}</div>}

                    <Elements.Button type="submit" variant="primary" size="sm" className="w-full" block>
                        Shop Plans
                    </Elements.Button>
                </form>
            ),
        },
        {
            value: 'state-county',
            label: 'Browse by State & County',
            content: (
                <form onSubmit={handleFormSubmit} className="flex min-w-0 flex-col gap-2 sm:gap-4" style={{ overflow: 'visible' }}>
                    <div
                        className="grid w-full min-w-0 grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-4"
                        style={{ overflow: 'visible' }}
                    >
                        <div className="relative z-50 w-full" style={{ overflow: 'visible' }}>
                            <StateSelect
                                id="state"
                                label="State"
                                value={selectedState}
                                onChange={(value) => {
                                    setSelectedState(value);
                                    if (fieldErrors.state) {
                                        setFieldErrors((prev) => ({ ...prev, state: false }));
                                        setValidationError(null);
                                    }
                                }}
                                placeholder="Select state"
                                error={fieldErrors.state ? '' : undefined}
                                btnClassName={fieldErrors.state ? '!border-red-500' : ''}
                                maxHeight="210px"
                            />
                        </div>
                        <div className="relative z-50 w-full" style={{ overflow: 'visible' }}>
                            <Select
                                id="county"
                                label="County (Optional)"
                                options={countyOptions}
                                value={selectedCounty}
                                onChange={(value) => setSelectedCounty(value as string)}
                                placeholder={
                                    isLoadingCounties ? 'Loading...' : selectedState ? 'Select county' : 'Select county'
                                }
                                disabled={isLoadingCounties}
                                searchable={true}
                                maxHeight="210px"
                            />
                        </div>
                    </div>
                    {validationError && <div className="-mt-2 text-sm font-medium text-red-600">{validationError}</div>}
                    <Elements.Button type="submit" variant="primary" size="sm" className="w-full" block>
                        Shop Plans
                    </Elements.Button>
                </form>
            ),
        },
    ];

    return <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />;
};

export default ShopPlansTabs;
